import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { api } from '../../lib/api/client';

export interface ChatMessage {
  id: string;
  roomId: string;
  text: string;
  senderId: string;
}

interface ChatState {
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
}

const initialState: ChatState = { messages: [], loading: false, error: null };

// Helper to standardize headers for chat API
function getTenantHeaders() {
  if (typeof window === 'undefined') {
    return {
      'Content-Type': 'application/json',
      'Authorization': '',
      'X-Tenant-Id': '',
      'X-Subdomain': '',
    };
  }
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${window.localStorage.getItem('token') || ''}`,
    'X-Tenant-Id': window.localStorage.getItem('tenantId') || '',
    'X-Subdomain': window.localStorage.getItem('subdomain') || '',
  };
}

export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async (roomId: string) => {
    const data = await api.getChatMessages(roomId);
    return data as ChatMessage[];
  }
);

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ roomId, text }: { roomId: string; text: string }) => {
    const data = await api.sendChatMessage(roomId, { text });
    return data as ChatMessage;
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action: PayloadAction<ChatMessage[]>) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load messages';
      })
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action: PayloadAction<ChatMessage>) => {
        state.messages.push(action.payload);
        state.loading = false;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to send message';
      });
  },
});

export const { clearError } = chatSlice.actions;
export default chatSlice.reducer;
